import React, { FC, ReactNode } from 'react';

import { EXTERNAL_PATHS } from 'src/common/constants';
import getClassName from 'src/common/utils/getClassName';
import captureException from 'src/common/utils/captureException';
import UniversalLink from '../UniversalLink';
import ActionsDropdown from '../ActionsDropdown';
import type { DropdownOption } from '../DropdownDS22';
import IconDS22, { IconIds } from '../IconDS22';
import s from './Navigation.module.scss';

const ALLOWED_DESTINATIONS = [
  EXTERNAL_PATHS.ADMIN_LISTINGS,
  EXTERNAL_PATHS.ADMIN_ADDONS,
  EXTERNAL_PATHS.CREATE_DIRECT_BOOKING,
  EXTERNAL_PATHS.CHARGE_ADD_ON,
];

const handleDropdownItemSelect = (selectedOption: DropdownOption) => {
  if (!selectedOption?.id) {
    captureException(new Error('Dropdown NavItem: selected option is missing an ID'), {
      extra: { selectedOption },
    });
    return;
  }

  if (!ALLOWED_DESTINATIONS.includes(selectedOption.id)) {
    captureException(new Error('Dropdown NavItem: selected option has an invalid ID'), {
      extra: { selectedOption },
    });
    return;
  }

  window.location.assign(selectedOption.id);
};

type BaseNavItemProps = {
  children: ReactNode;
  classNameModifier?: string;
  icon?: string | boolean;
  openInNewTab?: boolean;
};

type DropdownNavItemProps = BaseNavItemProps & {
  options: DropdownOption[];
  to?: never;
};

type LinkNavItemProps = BaseNavItemProps & {
  to: string;
  options?: never;
};

type Props = LinkNavItemProps | DropdownNavItemProps;

/**
 * NavItem component supports two modes:
 * 1. Link mode: Rendered when the `to` prop is provided.
 *    Displays a navigation link using UniversalLink.
 *    Use this mode for standard navigation to internal or external URLs.
 * 2. Dropdown mode: Rendered when the `options` prop is provided.
 *    Displays a dropdown menu using ActionsDropdown.
 *    Use this mode for navigation items that require multiple selectable destinations.
 * Only one of `to` or `options` should be provided at a time.
 */
const NavItem: FC<Props> = ({
  children,
  classNameModifier = '',
  icon,
  openInNewTab,
  options,
  to,
}) => {
  const content = (
    <>
      {icon && (
        <IconDS22
          id={icon as IconIds}
          size="l"
          className={getClassName(s, 'iconDS22', classNameModifier)}
        />
      )}
      <span className={getClassName(s, 'children', classNameModifier)}>{children}</span>
    </>
  );

  if (!options && !to) {
    if (process.env.NODE_ENV === 'development') {
      // Warn developers about misconfigured NavItem
      // eslint-disable-next-line no-console
      console.warn('NavItem: Both "options" and "to" props are missing. This NavItem will not render.', {
        children,
        classNameModifier,
        icon,
        openInNewTab,
        options,
        to,
      });
    }
    return null;
  }

  return (
    <li className={getClassName(s, 'item', classNameModifier)}>
      {to && (
        <UniversalLink
          to={to}
          openInNewTab={openInNewTab}
          className={getClassName(s, 'link', classNameModifier)}
          activeClassName={s.link_active}
        >
          {content}
        </UniversalLink>
      )}
      {options && (
        <ActionsDropdown
          dropdownPosition="right"
          label={content}
          handleSelect={handleDropdownItemSelect}
          options={options}
          showCaret={false}
          variant="topNav"
        />
      )}
    </li>
  );
};

export default NavItem;
